import { isDeepEqual } from "remeda"

export const MAX_TITLEBAR_HISTORY = 100

export type TitlebarAction = "back" | "forward" | undefined

export type HistoryLocation = { url: string; state?: unknown }

export type HistoryNavigation = "push" | "replace" | { index: number }

export type TitlebarHistory = {
  stack: HistoryLocation[]
  index: number
  action: TitlebarAction
}

export function applyPath(
  state: TitlebarHistory,
  current: HistoryLocation,
  navigation: HistoryNavigation = "push",
  max = MAX_TITLEBAR_HISTORY,
): TitlebarHistory {
  if (!state.stack.length) {
    return { stack: [current], index: 0, action: undefined }
  }

  if (typeof navigation === "object") {
    // Application Back preserves route state through URL navigation. A later
    // browser traversal can therefore have a different delta from this stack.
    const matching = state.stack.flatMap((entry, index) => (entry.url === current.url ? [index] : []))
    const exact = matching.filter((index) => matchesHistoryState(state.stack[index].state, current.state))
    const index = (exact.length ? exact : matching).reduce(
      (nearest, index) =>
        nearest === -1 || Math.abs(index - navigation.index) < Math.abs(nearest - navigation.index) ? index : nearest,
      -1,
    )
    if (index === -1) return { stack: [current], index: 0, action: undefined }
    return {
      stack: state.stack.map((entry, position) => (position === index ? current : entry)),
      index,
      action: undefined,
    }
  }

  const active = state.stack[state.index]
  if (navigation === "replace" || current.url === active.url) {
    if (!state.action && current.url === active.url && current.state === active.state) return state
    return {
      ...state,
      stack: state.stack.map((entry, index) => (index === state.index ? current : entry)),
      action: undefined,
    }
  }

  if (state.action) return { ...state, action: undefined }

  return pushPath(state, current, max)
}

function matchesHistoryState(saved: unknown, current: unknown) {
  if (isDeepEqual(saved, current)) return true
  if (!saved || !current || typeof saved !== "object" || typeof current !== "object") return false
  // Browser integrations can append history metadata to the application's state.
  return Object.entries(saved).every(([key, value]) => isDeepEqual(value, (current as Record<string, unknown>)[key]))
}

export function pushPath(state: TitlebarHistory, path: HistoryLocation, max = MAX_TITLEBAR_HISTORY): TitlebarHistory {
  const stack = state.stack.slice(0, state.index + 1).concat(path)
  const next = trimHistory(stack, stack.length - 1, max)
  return { ...state, ...next, action: undefined }
}

export function trimHistory(stack: HistoryLocation[], index: number, max = MAX_TITLEBAR_HISTORY) {
  if (stack.length <= max) return { stack, index }
  const cut = stack.length - max
  return {
    stack: stack.slice(cut),
    index: Math.max(0, index - cut),
  }
}

export function backPath(state: TitlebarHistory) {
  if (state.index <= 0) return
  const index = state.index - 1
  const to = state.stack[index]
  if (!to) return
  return { state: { ...state, index, action: "back" as const }, to }
}

export function forwardPath(state: TitlebarHistory) {
  if (state.index >= state.stack.length - 1) return
  const index = state.index + 1
  const to = state.stack[index]
  if (!to) return
  return { state: { ...state, index, action: "forward" as const }, to }
}
